import {IconButton} from '@dropbox/dig-components/buttons';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronLeftLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';
import {useEffect, useMemo, useState} from 'react';
import {useScorecards, useScorecardSnapshots} from 'views/scorecards/hooks';
import {
  buildScorecardSectionsFromApi,
  formatEndOfQuarterLabel,
  formatQuarterLabel,
  formatSnapshotLabel,
  getScorecardSections,
  parseApiDate,
} from 'views/scorecards/scorecardData';
import {ScorecardQuarterCloseSelector} from 'views/scorecards/ScorecardQuarterCloseSelector';
import {ScorecardsTable} from 'views/scorecards/ScorecardsTable';

import {EmptyState} from './WorkstreamsControls';

const CURRENT = 'current';

const normalize = (value?: string | null) => (value ?? '').trim().toLocaleLowerCase();

/**
 * /workstreams?extra=1: the Scorecards table driven by the
 * workstreams left rail. The selected pillar picks the matching scorecard tab
 * (Company otherwise); the snapshot stepper and tabs are local state, so
 * nothing navigates away from the host page.
 */
export const EmbeddedScorecards = ({pillar}: {pillar: string | null}) => {
  const [snapshotDt, setSnapshotDt] = useState(CURRENT);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const {data: snapshotsResponse} = useScorecardSnapshots();
  const snapshotOptions = useMemo(
    () => [CURRENT, ...(snapshotsResponse?.snapshots ?? [])],
    [snapshotsResponse?.snapshots]
  );
  const endOfQuarterSnapshots = snapshotsResponse?.end_of_quarter_snapshots ?? [];
  const snapshotIndex = snapshotOptions.indexOf(snapshotDt);

  const {data: response, isLoading, isError} = useScorecards(snapshotDt);
  const tabs = useMemo(() => response?.sections ?? [], [response?.sections]);
  const isEndOfQuarter = response?.is_end_of_quarter ?? endOfQuarterSnapshots.includes(snapshotDt);

  // Follow the rail: a selected pillar opens its tab, otherwise the first (Company).
  const pillarTabId = pillar
    ? (tabs.find((tab) => normalize(tab.label) === normalize(pillar))?.id ?? null)
    : null;
  useEffect(() => {
    setSelectedTab(pillarTabId);
  }, [pillarTabId]);
  const activeTab =
    selectedTab && tabs.some((tab) => tab.id === selectedTab) ? selectedTab : (tabs[0]?.id ?? null);

  const baseSections = useMemo(
    () => buildScorecardSectionsFromApi(response?.metrics, tabs),
    [response?.metrics, tabs]
  );
  const sections = useMemo(
    () => getScorecardSections(activeTab, baseSections),
    [activeTab, baseSections]
  );
  const showSectionHeaders =
    baseSections.find((section) => section.id === activeTab)?.grouped ?? false;

  // Column labels mirror the /scorecards page (snapshot vs. the prior snapshot).
  const [currentPeriod, priorPeriod] = response?.snapshot_periods ?? [];
  const currentDate = parseApiDate(currentPeriod);
  const priorDate = parseApiDate(priorPeriod);
  const currentQuarterLabel = formatQuarterLabel(
    currentPeriod === CURRENT ? new Date() : currentDate
  );
  const currentDateLabel = isEndOfQuarter
    ? `${currentQuarterLabel} Actual`
    : currentPeriod === CURRENT
      ? 'Current'
      : currentDate
        ? formatSnapshotLabel(currentDate)
        : '';
  const priorDateLabel = isEndOfQuarter
    ? null
    : priorDate
      ? currentPeriod === CURRENT
        ? 'Previous'
        : formatSnapshotLabel(priorDate)
      : null;

  const selectedDate = parseApiDate(snapshotDt);
  const snapshotLabel =
    snapshotDt === CURRENT
      ? 'Current'
      : selectedDate
        ? isEndOfQuarter
          ? formatEndOfQuarterLabel(selectedDate)
          : formatSnapshotLabel(selectedDate)
        : '';
  const canGoOlder = snapshotIndex >= 0 && snapshotIndex < snapshotOptions.length - 1;
  const canGoNewer = snapshotIndex > 0;
  const hasAnyMetrics = baseSections.some((section) => section.metrics.length > 0);

  return (
    <Box paddingX="24" paddingY="16">
      <Stack gap="16">
        <Split gap="16" alignY="center">
          <Split.Item>
            <Title size={18}>{t('scorecards')}</Title>
          </Split.Item>
          <Split.Item width="hug">
            <Box display="flex" alignItems="center" paddingX="8" style={{gap: 16}}>
              <IconButton
                aria-label="Previous snapshot"
                variant={canGoOlder ? 'filled' : 'opacity'}
                size="small"
                disabled={!canGoOlder}
                className={canGoOlder ? undefined : atoms({color: 'Disabled On Surface'})}
                onClick={() => setSnapshotDt(snapshotOptions[snapshotIndex + 1])}
              >
                <UIIcon src={ChevronLeftLine} />
              </IconButton>
              <Box paddingX="4" style={{minWidth: 60, textAlign: 'center'}}>
                <Text size="medium" isBold>
                  {snapshotLabel || '--'}
                </Text>
              </Box>
              <IconButton
                aria-label="Next snapshot"
                variant={canGoNewer ? 'filled' : 'opacity'}
                size="small"
                disabled={!canGoNewer}
                className={canGoNewer ? undefined : atoms({color: 'Disabled On Surface'})}
                onClick={() => setSnapshotDt(snapshotOptions[Math.max(0, snapshotIndex - 1)])}
              >
                <UIIcon src={ChevronRightLine} />
              </IconButton>
            </Box>
          </Split.Item>
          <Split.Item width="fill" />
          <Split.Item width="hug">
            <ScorecardQuarterCloseSelector
              endOfQuarterSnapshots={endOfQuarterSnapshots}
              selectedSnapshotDt={snapshotDt}
              onSelect={setSnapshotDt}
            />
          </Split.Item>
        </Split>

        <Tabs selectedTab={activeTab ?? ''} onSelection={setSelectedTab}>
          <Tabs.Group>
            {tabs.map((tab) => (
              <Tabs.Tab key={tab.id} id={tab.id}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.Group>
        </Tabs>

        {isError ? (
          <EmptyState title="Could not load scorecards" />
        ) : !isLoading && !hasAnyMetrics ? (
          <EmptyState
            title={t('scorecard_period_empty_title')}
            detail={t('scorecard_period_empty_body')}
          />
        ) : (
          <ScorecardsTable
            sections={sections}
            showSectionHeaders={showSectionHeaders}
            snapshotIndex={0}
            snapshotKey={snapshotDt}
            currentDateLabel={currentDateLabel}
            priorDateLabel={priorDateLabel}
            currentQuarterLabel={currentQuarterLabel}
            isEndOfQuarter={isEndOfQuarter}
            loading={isLoading}
            scrollMode="self"
          />
        )}
      </Stack>
    </Box>
  );
};
